<template>
    <transition name="fade" @after-enter="showModalDialog = true">
        <div
            v-show="visible"
            class="ca-modal modal media-select-modal"
            :class="{'is-visible': visible}"
            tabindex="-1"
            role="dialog"
            aria-labelledby="selectModalLabel"
            :aria-hidden="!visible">

            <transition name="slideDown">
                <div v-show="showModalDialog" class="modal-dialog modal-xl">
                    <div class="modal-content">
                        <!-- Modal Header //-->
                        <div class="modal-header">

                            <div class="modal-header-info">
                                <h4 class="modal-title" id="selectModalLabel" v-html="title"></h4>
                                <p v-if="description" v-html="description"></p>
                            </div>

                            <div class="modal-header-actions">
                                <button class="btn btn-default btn-upload mr-1" v-if="options.upload.allow" @click="toggleUpload" v-html="uploadButtonText"></button>
                                <media-select-filters class="mr-1" v-if="hasStrategy && currentView === 'MediaSelect'" :disabled="isLoading" :filter-options="strategy.filterOptions" v-on:filters-set="getData" />
                                <button class="btn btn-default" v-if="showUseSelected && selectedCount > 0" @click.stop="clearSelected">
                                    <i class="fas fa-ban"></i>
                                    Clear selected
                                </button>
                                <button v-show="showUseSelected" class="btn btn-primary btn-use-selected" :disabled="disableUseSelected" @click="setActionCalled(true)">
                                    <i class="fa" :class="useSelectedClasses"></i>
                                    {{ useSelectedText }}
                                </button>
                                <button type="button" class="close btn-close" aria-hidden="true" @click="doDismiss"><i class="fa fa-times"></i></button>
                            </div>

                        </div>

                        <!-- Modal Body //-->
                        <div class="modal-body" :class="bodyClasses">
                            <component
                                v-if="!isLoading"
                                :is="currentView"
                                :items="items"
                                :item-id="options.itemId"
                                :fetch-options="this.fetchOptions"
                                :meta="meta"
                                :clear-selected="selectedCleared"
                                @selected-cleared="selectedCleared = false"
                            />

                            <spinner :show="isLoading" :message="loadingMessage" />
                        </div>

                        <!-- Modal Footer //-->
                        <div class="modal-footer">
                            <button type="button" class="btn btn-default waves-effect" @click="doDismiss"><i class="fa fa-times"></i> Close</button>
                            <button v-show="showUseSelected" class="btn btn-primary btn-use-selected" :disabled="disableUseSelected" @click="setActionCalled(true)">
                                <i class="fa" :class="useSelectedClasses"></i>
                                {{ useSelectedText }}
                            </button>
                        </div>
                    </div>
                </div>
            </transition>
        </div>
    </transition>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex';

// components
import MediaSelect from '@/components/media/select';
import Spinner from '@/components/media/Spinner';
import MediaSelectFilters from '@/components/media/filters/MediaSelect';

// mixins
import upload from '@/components/media/modals/media-select-modal/mixins/upload';

export default {
    name: 'MediaSelectModal',
    mixins: [
        upload,
    ],
    components: {
        MediaSelect,
        Spinner,
        MediaSelectFilters,
    },
    data() {
        return {
            showModalDialog: false,
            selectedCleared: false,
        }
    },
    computed: {
        ...mapGetters({
            options: 'getMediaSelectOptions',
            items: 'getMediaSelectItems',
            visible: 'getMediaSelectModalShow',
            strategy: 'getMediaSelectStrategy',
            validator: 'getMediaSelectValidator',
            currentView: 'getMediaSelectModalCurrentView',
            fetchOptions: 'getMediaSelectFetchOptions',
            meta: 'getMediaSelectItemsMeta',
            isLoading: 'getMediaSelectIsLoading',
            actionCalled: 'getMediaSelectActionCalled',
            loadingMessage: 'getMediaSelectLoadingMessage',
            selectedCount: 'getMediaSelectSelectedCount',
            currentFilter: 'getMediaSelectCurrentFilter',
        }),
        hasStrategy() {
            return Object.keys(this.strategy).length > 0;
        },
        title() {
            return this.options.title;
        },
        description() {
            return this.options.hasOwnProperty('description') && this.options.description;
        },
        showUseSelected() {
            return this.currentView === 'MediaSelect';
        },
        disableUseSelected() {
            return this.actionCalled || (!this.validator || this.validator.selectedCount.$error);
        },
        useSelectedClasses() {
            return {
                'fa-circle-o-notch fa-spin': this.actionCalled,
                'fa-check': !this.actionCalled,
            }
        },
        useSelectedText() {
            let text = 'Use selected';
            if (this.actionCalled) {
                text = 'Processing...';
            }
            return text;
        },
        bodyClasses() {
            return {
                'is-processing': this.actionCalled,
            }
        }
    },
    watch: {
        visible(newValue) {
            const commFrame = document.querySelector('.comm-frame');

            if (newValue) {
                this.getData();
                document.body.classList.add('modal-open');
                if (commFrame) {
                    commFrame.classList.add('is-disabled');
                }
            } else {
                document.body.classList.remove('modal-open');
                if (commFrame) {
                    commFrame.classList.remove('is-disabled');
                }
            }
        },
        options: {
            immediate: true,
            handler(newValue) {
                if (newValue.sets) this.fetchOptions.sets = newValue.sets;
                if (newValue.media) this.fetchOptions.media = newValue.media;
            }
        },
    },
    methods: {
        ...mapActions({
            getItems: 'fetchMediaSelectItems',
            hide: 'hideMediaSelectModal',
            setItems: 'addMediaSelectItems',
            clearMediaSelectSelected: 'clearMediaSelectSelected',
        }),
        ...mapMutations({
            show: 'setMediaSelectModalShow',
            setIsLoading: 'setMediaSelectIsLoading',
            setCurrentView: 'setMediaSelectModalCurrentView',
            setActionCalled: 'setMediaSelectActionCalled',
            setSelectedItems: 'setMediaSelectSelectedItems',
            setCurrentFilter: 'setMediaSelectCurrentFilter',
        }),
        getData() {
            // Intial load of media items / sets
            this.setCurrentFilter(this.currentFilter);
            this.fetchOptions.filters = this.currentFilter;
            this.fetchOptions.page = 1;

            this.setIsLoading(true);
            this.getItems(this.fetchOptions).then(response => {
                this.setItems(response);
                this.setIsLoading(false);
            });
        },
        doDismiss() {
            this.showModalDialog = false;
            this.hide();
        },
        clearSelected() {
            this.clearMediaSelectSelected().then(() => {
                this.selectedCleared = true;
            });
        },
    }
}
</script>

<style lang="scss" scoped>
.modal-body {
    max-height: calc(100vh - 235px);
    overflow-y: auto;
}

.modal-header {
    padding-bottom: 0;
}

.modal-header-actions {
    display: flex;
    flex-direction: row;
    align-items: center;
}
</style>


